import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ANONYMOUS_SESSION_CHANGED_EVENT, getAnonymousSession } from '../lib/anonymousSession';
import type { UserSettingsPayload } from '../lib/apiClient';
import { useApi } from '../hooks/useApi';
import { useAuth } from './AuthContext';
import {
  DEFAULT_USER_SETTINGS,
  normalizeUserSettings,
  serializeUserSettings,
  type UserSettings,
} from '../types/userSettings';

type UserSettingsContextValue = {
  settings: UserSettings;
  isLoading: boolean;
  saveSettings: (nextSettings: UserSettings) => Promise<boolean>;
};

const UserSettingsContext = createContext<UserSettingsContextValue | undefined>(undefined);

export function useUserSettings(): UserSettingsContextValue {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }

  return context;
}

type UserSettingsProviderProps = {
  children: ReactNode;
};

export function UserSettingsProvider({ children }: UserSettingsProviderProps) {
  const { user } = useAuth();
  const { getUserSettings, saveUserSettings } = useApi();
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_USER_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  const [anonymousSession, setAnonymousSessionState] = useState(() => getAnonymousSession());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncAnonymousSession = () => {
      setAnonymousSessionState(getAnonymousSession());
    };

    window.addEventListener(ANONYMOUS_SESSION_CHANGED_EVENT, syncAnonymousSession);
    window.addEventListener('storage', syncAnonymousSession);

    return () => {
      window.removeEventListener(ANONYMOUS_SESSION_CHANGED_EVENT, syncAnonymousSession);
      window.removeEventListener('storage', syncAnonymousSession);
    };
  }, []);

  const hasSession = Boolean(user || (anonymousSession?.userId && anonymousSession?.accountId));

  const requestPayload = useMemo(() => {
    if (!hasSession) {
      return null;
    }

    return {
      account_id: anonymousSession?.accountId,
      anonymous_user_id: anonymousSession?.userId,
      anonymous_account_id: anonymousSession?.accountId,
    };
  }, [anonymousSession?.accountId, anonymousSession?.userId, hasSession]);

  useEffect(() => {
    if (!requestPayload) {
      setSettings(DEFAULT_USER_SETTINGS);
      return;
    }

    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      const result = await getUserSettings(requestPayload, {
        shouldShowError: (error) => error.code !== 'UNAUTHORIZED',
      });

      if (!isCancelled) {
        if (result.ok) {
          setSettings(normalizeUserSettings(result.data.settings));
        } else {
          setSettings(DEFAULT_USER_SETTINGS);
        }

        setIsLoading(false);
      }
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [getUserSettings, requestPayload]);

  const save = useCallback(async (nextSettings: UserSettings): Promise<boolean> => {
    if (!requestPayload) {
      setSettings(nextSettings);
      return true;
    }

    const normalizedSettings = normalizeUserSettings(serializeUserSettings(nextSettings));
    const serializedSettings = serializeUserSettings(normalizedSettings) as UserSettingsPayload;
    setSettings(normalizedSettings);

    const result = await saveUserSettings({
      ...requestPayload,
      settings: serializedSettings,
    });

    if (!result.ok) {
      return false;
    }

    setSettings(normalizeUserSettings(result.data.settings));
    return true;
  }, [requestPayload, saveUserSettings]);

  const value = useMemo<UserSettingsContextValue>(() => ({
    settings,
    isLoading,
    saveSettings: save,
  }), [isLoading, save, settings]);

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
}
